import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Segmented, Space, Spin, Typography, Button, Tooltip, theme } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { ok, formatBps, formatMB } from './utils';
import AlertRulesCard from './AlertRulesCard';

const { Text } = Typography;

const RANGES = [
    { label: '1 hour', value: '1h' },
    { label: '6 hours', value: '6h' },
    { label: '24 hours', value: '24h' },
    { label: '7 days', value: '7d' },
    { label: '30 days', value: '30d' },
];


const GRANS = [
    { label: 'Hourly', value: 'hour' },
    { label: 'Daily', value: 'day' },
    { label: 'Monthly', value: 'month' },
];

const fmtBytes = (b) => {
    const n = Number(b) || 0;
    if (n >= 1024 ** 4) return `${(n / 1024 ** 4).toFixed(2)} TB`;
    if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
    if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024).toFixed(0)} KB`;
};

/** Hour/day/month public traffic bars (from evm_vm_traffic_hourly). */
export const TrafficStats = ({ load, isMobile, active = true }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [gran, setGran] = useState('day');
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!active) return;
        let alive = true;
        load(gran).then((res) => { if (alive) setData(ok(res) ? res.data : { points: [] }); });
        return () => { alive = false; };
    }, [gran, load, active]);
    const points = data?.points || [];
    const option = {
        grid: { left: 8, right: 16, top: 28, bottom: 8, containLabel: true },
        legend: { top: 0, right: 0, textStyle: { color: token.colorTextSecondary }, itemWidth: 12, itemHeight: 8 },
        tooltip: { trigger: 'axis', backgroundColor: token.colorBgElevated, borderColor: token.colorBorderSecondary, textStyle: { color: token.colorText }, valueFormatter: fmtBytes },
        xAxis: { type: 'category', data: points.map((p) => (gran === 'hour' ? p.t.slice(5) : p.t)), axisLabel: { color: token.colorTextSecondary, hideOverlap: true }, axisLine: { lineStyle: { color: token.colorBorder } } },
        yAxis: { type: 'value', axisLabel: { color: token.colorTextSecondary, formatter: fmtBytes }, splitLine: { lineStyle: { color: token.colorBorderSecondary } } },
        series: [
            { name: t('In'), type: 'bar', stack: 't', data: points.map((p) => p.in_bytes), itemStyle: { color: token.colorPrimary } },
            { name: t('Out'), type: 'bar', stack: 't', data: points.map((p) => p.out_bytes), itemStyle: { color: token.colorSuccess } },
        ],
    };
    return (
        <Card
            size="small"
            title={t('Traffic statistics')}
            extra={<Segmented size="small" options={GRANS.map(o => ({ ...o, label: t(o.label) }))} value={gran} onChange={setGran} />}
        >
            {data && (
                <Space size={isMobile ? 12 : 24} wrap style={{ marginBottom: 8 }}>
                    <span><Text type="secondary">{t('In ')}</Text>{fmtBytes(data.total?.in_bytes)}</span>
                    <span><Text type="secondary">{t('Out ')}</Text>{fmtBytes(data.total?.out_bytes)}</span>
                    {(data.total?.pri_in_bytes || 0) + (data.total?.pri_out_bytes || 0) > 0 && (
                        <span><Text type="secondary">{t('Private network ')}</Text>{fmtBytes((data.total.pri_in_bytes || 0) + (data.total.pri_out_bytes || 0))}</span>
                    )}
                </Space>
            )}
            <ReactECharts option={option} notMerge style={{ height: isMobile ? 200 : 240 }} />
        </Card>
    );
};

const MonitorTab = ({ id, api, isMobile, active }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [range, setRange] = useState('1h');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.getVmMetrics(id, range);
            setData(ok(res) ? res.data : { available: false, error: true });
        } finally {
            setLoading(false);
        }
    }, [api, id, range]);

    useEffect(() => {
        if (!active) return undefined;
        load();
        if (range !== '1h') return undefined;
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [load, range, active]);

    const loadTraffic = useCallback((g) => api.getVmTrafficSeries(id, g), [api, id]);
    const points = data?.points || [];
    const timeFmt = range === '7d' || range === '30d' ? 'MM-DD HH:mm' : 'HH:mm';

    const baseOption = useCallback((series, yFormatter, extra = {}) => ({
        grid: { left: 8, right: 16, top: 28, bottom: 8, containLabel: true },
        legend: { top: 0, right: 0, textStyle: { color: token.colorTextSecondary }, itemWidth: 12, itemHeight: 8 },
        tooltip: {
            trigger: 'axis',
            backgroundColor: token.colorBgElevated,
            borderColor: token.colorBorderSecondary,
            textStyle: { color: token.colorText },
            valueFormatter: yFormatter,
        },
        xAxis: {
            type: 'time',
            axisLine: { lineStyle: { color: token.colorBorder } },
            axisLabel: { color: token.colorTextSecondary, formatter: (v) => dayjs(v).format(timeFmt), hideOverlap: true },
            splitLine: { show: false },
        },
        yAxis: {
            type: 'value',
            min: 0,
            axisLabel: { color: token.colorTextSecondary, formatter: yFormatter },
            splitLine: { lineStyle: { color: token.colorBorderSecondary } },
            ...extra.yAxis,
        },
        series: series.map((s) => ({
            type: 'line',
            showSymbol: false,
            smooth: true,
            lineStyle: { width: 1.5, ...(s.dashed ? { type: 'dashed' } : {}) },
            areaStyle: s.area ? { opacity: 0.12 } : undefined,
            ...s,
            data: points.map((p) => [p.t * 1000, s.pick(p)]),
        })),
        color: [token.colorPrimary, token.colorSuccess, token.colorWarning],
    }), [points, token, timeFmt]);

    const charts = useMemo(() => [
        {
            title: 'CPU',
            option: baseOption(
                [{ name: 'CPU', pick: (p) => p.cpu, area: true }],
                (v) => `${(Number(v) || 0).toFixed(1)}%`,
                { yAxis: { max: (v) => Math.max(100, Math.ceil(v.max)) } },
            ),
        },
        {
            title: t('Memory'),
            option: baseOption(
                [
                    { name: t('Used'), pick: (p) => p.mem_used_mb, area: true },
                    { name: t('Total'), pick: (p) => p.mem_total_mb, dashed: true },
                ],
                (v) => formatMB(v),
            ),
        },
        {
            title: t('Disk read/write'),
            option: baseOption(
                [
                    { name: t('Read'), pick: (p) => p.disk_read_bps },
                    { name: t('Write'), pick: (p) => p.disk_write_bps },
                ],
                formatBps,
            ),
        },
        {
            title: t('Public network traffic'),
            option: baseOption(
                [
                    { name: t('In'), pick: (p) => p.net_in_bps, area: true },
                    { name: t('Out'), pick: (p) => p.net_out_bps, area: true },
                ],
                formatBps,
            ),
        },
        // Only VMs with VPC traffic get the private chart (an all-zero chart is noise).
        points.some((p) => (p.pri_in_bps || 0) + (p.pri_out_bps || 0) > 0) && {
            title: t('Private network traffic'),
            option: baseOption(
                [
                    { name: t('In'), pick: (p) => p.pri_in_bps },
                    { name: t('Out'), pick: (p) => p.pri_out_bps },
                ],
                formatBps,
            ),
        },
    ].filter(Boolean), [baseOption, points, t]);

    const traffic = data?.traffic;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <Segmented options={RANGES.map(o => ({ ...o, label: t(o.label) }))} value={range} onChange={setRange} size={isMobile ? 'small' : 'middle'} />
                </div>
                <Tooltip title={range === '1h' ? t('Refreshes automatically every 60 seconds') : t('Refresh')}>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} size={isMobile ? 'small' : 'middle'} />
                </Tooltip>
            </div>

            {traffic && (
                <Card size="small">
                    <Space size={isMobile ? 16 : 32} wrap>
                        <span><Text type="secondary">{t('Used this month ')}</Text><Text strong>{formatMB(traffic.used_mb)}</Text></span>
                        <span><Text type="secondary">{t('In ')}</Text>{formatMB(traffic.in_mb)}</span>
                        <span><Text type="secondary">{t('Out ')}</Text>{formatMB(traffic.out_mb)}</span>
                        <span><Text type="secondary">{t('Quota ')}</Text>{Number(traffic.limit_mb) > 0 ? formatMB(traffic.limit_mb) : t('Unlimited')}</span>
                    </Space>
                </Card>
            )}

            {api?.getVmTrafficSeries && (
                <TrafficStats load={loadTraffic} isMobile={isMobile} active={active} />
            )}

            <AlertRulesCard vmId={id} api={api} isMobile={isMobile} active={active} />

            {loading && !data ? (
                <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
            ) : (
                <Row gutter={[16, 16]}>
                    {charts.map((c) => (
                        <Col xs={24} xl={12} key={c.title}>
                            <Card size="small" title={c.title}>
                                <ReactECharts option={c.option} notMerge style={{ height: isMobile ? 200 : 240 }} />
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Space>
    );
};

export default MonitorTab;
